import { getAppId } from '@shared/composables/useAppContext';
import { createI18n } from 'vue-i18n';
import { apiFetchUrl, apiUrl, localeMapping, setApiLocale } from '@shared/http/client';

export const i18n = createI18n({
    legacy: false,
    locale: 'de',
    fallbackLocale: 'de',
    // Einzelne Keys tragen bewusst Markup (Angulars <x id="LINE_BREAK"/> wird als <br /> migriert,
    // siehe APP_MIGRATE.md §7). Sie werden per v-html gerendert; die Kataloge stammen ausschließlich
    // aus reviewten Repo-Dateien, weshalb die XSS-Warnung hier nur Rauschen wäre.
    warnHtmlMessage: false,
    messages: { de: {} },
});

// vue-i18n narrows the locale type to the statically declared key ('de'); real locales are
// loaded at runtime, so widen for assignments below.
type Locale = typeof i18n.global.locale.value;

const loadedLocales = new Set<string>();

export async function loadLocale(locale: string): Promise<void> {
    if (!locale) {
        return;
    }

    if (!loadedLocales.has(locale)) {
        const symfonyLocale = localeMapping[locale] ?? `${locale}_${locale.toUpperCase()}`;
        // appId uniquely identifies the app (set on kgrDynamicConfig in main.ts from APP_CONFIG);
        // the catalogue endpoint lives under the `/apis/*` CORS scope so it works on web + native.
        const appName = getAppId();
        const url = apiUrl(`/apis/translation/${appName}/messages?lang=${symfonyLocale}`);
        // fetch instead of injecting a <script>: apiFetchUrl attaches the bearer token (when
        // present) + X-App-Version, which a cross-origin <script src> tag cannot.
        try {
            const response = await apiFetchUrl(url);
            const messages = (await response.json()) as Record<string, string>;

            i18n.global.setLocaleMessage(locale, messages);
            loadedLocales.add(locale);
        } catch (error) {
            // A failing catalogue fetch (e.g. endpoint not yet deployed on this backend) must never
            // block app boot — `loadLocale` runs in the router guard that gates the initial mount.
            // Fall back to the German source keys; do not mark loaded so a later navigation retries.
            console.error('[i18n] failed to load locale catalogue', locale, error);
        }
    }
    i18n.global.locale.value = locale as Locale;
    // keep the API `lang=` param in sync with the displayed locale (web + native)
    setApiLocale(locale);
}
